<template>
  <div class="piece-selector">
    <h4 class="title is-6 mb-3">{{ isEraser ? "Erase" : "Place" }} Piece</h4>
    <div class="settings-field">
      <b-switch
        v-model="isEraser"
        passive-type="is-success"
        type="is-danger"
        size="is-small"
        @input="toggleEraser"
      >
        {{ isEraser ? "Erase" : "Place" }}
      </b-switch>
      <b-switch
        v-model="isWhiteSelected"
        passive-type="is-dark"
        type="is-info"
        size="is-small"
        :left-label="true"
        @input="toggleSelectedColor"
        :disabled="isEraser"
      >
        {{ selectedColor === CHESS_SIDE.WHITE ? "White" : "Black" }}
      </b-switch>
    </div>

    <div class="piece-grid">
      <div
        v-for="piece in chessPieces"
        :key="piece"
        class="piece-item"
        :class="{
          selected: selectedPiece === piece && !isEraser,
          'eraser-mode': isEraser,
        }"
        @click="selectPiece(piece)"
      >
        <span class="image is-48x48">
          <img :src="getPieceSvg(piece)" :alt="piece" />
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { CHESS_PIECES, CHESS_SIDE } from "vue-chess-shared/constants";
import { chessPieceSvg } from "@/utils/chess-svg";
import { getFenPiece } from "vue-chess-shared";

export default {
  name: "PieceSelector",

  filters: {
    chessPieceSvg,
  },

  computed: {
    fenPiece() {
      if (this.isEraser) return "Remove";
      return getFenPiece(this.selectedPiece, this.selectedColor);
    },
  },

  data() {
    return {
      chessPieces: Object.values(CHESS_PIECES),
      selectedColor: CHESS_SIDE.WHITE,
      isWhiteSelected: true,
      selectedPiece: null,
      isEraser: false,
      CHESS_SIDE,
    };
  },

  watch: {
    fenPiece() {
      if (this.isEraser) {
        this.$emit("eraserSelected");
        return;
      }
      const color = this.selectedColor?.toLowerCase();
      const role = this.selectedPiece?.toLowerCase();
      if (color && role) {
        this.$emit("pieceSelected", { color, role });
        return;
      }
      this.$emit("pieceSelected", null);
    },
  },

  methods: {
    getPieceSvg(pieceType) {
      const pieceColor = `${pieceType}_${
        this.isWhiteSelected ? CHESS_SIDE.WHITE : CHESS_SIDE.BLACK
      }`;
      return chessPieceSvg(pieceColor);
    },

    selectPiece(piece) {
      this.isEraser = false;
      if (this.selectedPiece === piece) {
        this.selectedPiece = null;
        return;
      }
      this.selectedPiece = piece;
    },

    toggleSelectedColor(value) {
      this.selectedColor = value ? CHESS_SIDE.WHITE : CHESS_SIDE.BLACK;
    },

    toggleEraser(value) {
      if (value) {
        // When switching to eraser mode
        this.previousColor = this.selectedColor;
        this.selectedColor = "";
        this.$emit("eraserSelected");
      } else {
        // When switching back to place mode
        this.selectedColor = this.isWhiteSelected
          ? CHESS_SIDE.WHITE
          : CHESS_SIDE.BLACK;
        const color = this.selectedColor?.toLowerCase();
        const role = this.selectedPiece?.toLowerCase();
        if (color && role) {
          this.$emit("pieceSelected", { color, role });
        }
      }
    },

    handleKeyDown(event) {
      // Check if any modal is open
      if (document.querySelector(".modal.is-active")) return;

      // Only process if not in an input field
      if (
        document.activeElement.tagName === "INPUT" ||
        document.activeElement.tagName === "TEXTAREA"
      ) {
        return;
      }

      const key = event.key.toUpperCase();

      switch (key) {
        case "ESCAPE":
          this.isEraser = false;
          this.selectedPiece = null;
          break;
        case "X":
          this.isEraser = !this.isEraser;
          this.toggleEraser(this.isEraser);
          break;
        case "S":
          this.isWhiteSelected = !this.isWhiteSelected;
          this.toggleSelectedColor(this.isWhiteSelected);
          break;
        // Piece selection shortcuts
        case "K":
          this.selectPiece("KING");
          break;
        case "Q":
          this.selectPiece("QUEEN");
          break;
        case "R":
          this.selectPiece("ROOK");
          break;
        case "B":
          this.selectPiece("BISHOP");
          break;
        case "N":
          this.selectPiece("KNIGHT");
          break;
        case "P":
          this.selectPiece("PAWN");
          break;
      }
    },
  },

  mounted() {
    // Add event listener for keydown events
    window.addEventListener("keydown", this.handleKeyDown);
  },

  beforeDestroy() {
    // Remove event listener when component is destroyed
    window.removeEventListener("keydown", this.handleKeyDown);
  },
};
</script>

<style scoped>
.piece-selector {
  padding: 0.75rem;
  width: 100%;
  height: 100%;
}

.settings-field {
  display: flex;
  justify-content: space-between;
  align-content: space-between;
  margin-bottom: 1rem;
  width: 100%;
}

.piece-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 0.5rem;
  min-height: 120px;
}

.piece-item {
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s, box-shadow 0.2s;
  padding: 0.5rem;
  aspect-ratio: 1; /* Makes each item square */
}

.piece-item:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.piece-item.selected {
  background-color: rgba(72, 199, 142, 0.2);
  box-shadow: 0 0 0 2px rgba(72, 199, 142, 0.5);
}

.piece-item.eraser-mode {
  background-color: rgba(241, 70, 104, 0.1);
  box-shadow: 0 0 0 1px rgba(241, 70, 104, 0.3);
}

.piece-item.eraser-mode:hover {
  background-color: rgba(241, 70, 104, 0.2);
  box-shadow: 0 0 0 2px rgba(241, 70, 104, 0.5);
  cursor: not-allowed;
}
</style>
